import { useEffect } from 'react';

/**
 * Closes the left navigation drawer when the user clicks into an embedded iframe.
 *
 * ClickAwayListener cannot observe clicks inside an iframe, so clicking an embedded report or video
 * would otherwise leave the drawer open. Clicking an iframe moves focus into it, which blurs the
 * parent window, so a window blur with a matching iframe as the active element stands in for "the
 * user clicked into the embed".
 *
 * @param {boolean} isOpen Whether the drawer is currently open. The listener is only attached while open.
 * @param {() => void} closeDrawer Callback that closes the drawer.
 * @param {string} iframeSelector Selector the focused iframe must match to count as a click into an embed.
 */
export function useCloseDrawerOnIframeFocus(
  isOpen: boolean,
  closeDrawer: () => void,
  iframeSelector: string
): void {
  useEffect(() => {
    if (!isOpen) return;

    let pendingCheck: number | undefined;

    const handleWindowBlur = (): void => {
      // Focus lands on the iframe asynchronously in some browsers, so read it on the next tick.
      pendingCheck = window.setTimeout(() => {
        const activeElement = document.activeElement;
        if (!(activeElement instanceof HTMLIFrameElement)) return;
        if (!activeElement.matches(iframeSelector)) return;
        // Off-screen dashboards stay mounted at full size behind `pointer-events: none`. The user
        // cannot have clicked one, so treat focus landing there as programmatic and ignore it.
        if (window.getComputedStyle(activeElement).pointerEvents === 'none') return;
        closeDrawer();
      }, 0);
    };

    window.addEventListener('blur', handleWindowBlur);
    return () => {
      window.clearTimeout(pendingCheck);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [isOpen, closeDrawer, iframeSelector]);
}
